import { Platform } from 'react-native';
import Config from 'react-native-config';
const productionConfig = {
    backendBaseUrl: 'https://app.abbisway.com',
    frontendBaseUrl: 'https://app.abbisway.com',
};
const testConfig = {
    backendBaseUrl: 'https://test.abbisway.com',
    frontendBaseUrl: 'https://test.abbisway.com',
};
/**
 * Store builds default to production, but `test` stays on test.abbisway.com so a
 * `test$` email can reach the test service from TestFlight and production builds.
 * `local` stays on production in store builds (no localhost in the shipped binary).
 * Localhost URLs live only in the `__STORE_BUILD__ === false` branch so Release
 * bundles can compile them out.
 */
const storeEnvironmentConfigs = {
    local: productionConfig,
    test: testConfig,
    prod: productionConfig,
};
export const ENVIRONMENT_CONFIGS = __STORE_BUILD__
    ? storeEnvironmentConfigs
    : {
        local: {
            backendBaseUrl: 'http://localhost:3000',
            frontendBaseUrl: 'http://localhost:8082',
        },
        test: testConfig,
        prod: productionConfig,
    };
export function environmentConfigs(storeBuild = __STORE_BUILD__) {
    if (storeBuild) {
        return storeEnvironmentConfigs;
    }
    return ENVIRONMENT_CONFIGS;
}
/** Only these accounts may use the `test$` prefix to reach TEST (case-insensitive). */
const TEST_PREFIX_ALLOWED = /koutavasj/i;
/**
 * True when `email` starts with `test$` and the rest names an allowed TEST account
 * (contains "koutavasj"). Any other `test$` email is treated as a production login.
 */
export function isAllowedTestPrefixEmail(email) {
    const trimmed = email?.trim() ?? '';
    return /^test\$/i.test(trimmed) && TEST_PREFIX_ALLOWED.test(trimmed.slice(5));
}
/**
 * Email to save and show in the sign-in field: a `test$` prefix that wouldn't reach
 * TEST (no "koutavasj") is dropped; everything else is kept as typed.
 */
export function sanitizeSavedSigninEmail(email) {
    const trimmed = email?.trim() ?? '';
    if (/^test\$/i.test(trimmed) && !isAllowedTestPrefixEmail(trimmed)) {
        return trimmed.slice(5);
    }
    return email ?? '';
}
/** Store-build session target. An allowed `test$` email keeps TEST across relaunch. */
export function resolveStoreBuildNetworkFields(email) {
    if (isAllowedTestPrefixEmail(email)) {
        return { environment: 'test', ...testConfig };
    }
    return { environment: 'prod', ...productionConfig };
}
/**
 * Store-build rehydrate (P5.2): where this session should point, and whether the
 * persisted tokens must be dropped. Tokens issued by one backend are useless on the
 * other, so when the persisted backend differs from the target (for example an old
 * build left the app on test.abbisway.com) the user signs in again on the target.
 */
export function resolveStoreBuildRehydrate(persisted) {
    const network = resolveStoreBuildNetworkFields(persisted.email);
    const strip = (u) => u.replace(/\/$/, '');
    const clearSession = !!persisted.backendBaseUrl &&
        strip(persisted.backendBaseUrl) !== strip(network.backendBaseUrl);
    return { network, clearSession };
}
/** P5.3: short label for the non-prod marker, or null on production. */
export function nonProdEnvironmentLabel(environment) {
    if (!environment || environment === 'prod') {
        return null;
    }
    return environment.toUpperCase();
}
// Default to local for development unless overridden via BACKEND_BASE_URL
export const DEFAULT_ENVIRONMENT = 'local';
/** Ensures a dev host string has a scheme and no trailing slash. */
export const normalizeDevHostUrl = (url) => {
    let normalized = url.trim();
    if (!/^https?:\/\//i.test(normalized)) {
        normalized = `http://${normalized}`;
    }
    return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
};
/**
 * Local API URL for the current runtime.
 * Web on localhost → localhost:3000; native with LOCAL_DEV_HOST → Mac LAN IP; else localhost (simulator).
 */
export function resolveLocalBackendBaseUrl() {
    if (__STORE_BUILD__) {
        return productionConfig.backendBaseUrl;
    }
    const webOverride = getWebLocalDevNetworkOverride();
    if (webOverride) {
        return webOverride.backendBaseUrl;
    }
    if (Platform.OS !== 'web') {
        const lanHost = Config.LOCAL_DEV_HOST?.trim();
        if (lanHost) {
            return normalizeDevHostUrl(lanHost);
        }
    }
    return ENVIRONMENT_CONFIGS.local.backendBaseUrl;
}
export function resolveBackendBaseUrl(environment) {
    if (environment === 'local') {
        return resolveLocalBackendBaseUrl();
    }
    return ENVIRONMENT_CONFIGS[environment].backendBaseUrl;
}
export function resolveEnvironmentConfig(environment) {
    return {
        backendBaseUrl: resolveBackendBaseUrl(environment),
        frontendBaseUrl: ENVIRONMENT_CONFIGS[environment].frontendBaseUrl,
    };
}
export function resolveDefaultEnvironmentConfig(storeBuild = __STORE_BUILD__, configuredBackendUrl = Config.BACKEND_BASE_URL, configuredFrontendUrl = Config.FRONTEND_BASE_URL, webLocalOverride = getWebLocalDevNetworkOverride()) {
    if (storeBuild) {
        return { environment: 'prod', ...productionConfig };
    }
    if (webLocalOverride) {
        return webLocalOverride;
    }
    const environment = inferEnvironmentFromBackendUrl(configuredBackendUrl);
    return {
        environment,
        backendBaseUrl: configuredBackendUrl || resolveBackendBaseUrl(environment),
        frontendBaseUrl: configuredFrontendUrl || ENVIRONMENT_CONFIGS[environment].frontendBaseUrl,
    };
}
/**
 * Infer environment from backend URL
 * This allows the build to set the correct environment via BACKEND_BASE_URL.
 */
export const inferEnvironmentFromBackendUrl = (backendUrl) => {
    if (__STORE_BUILD__) {
        return 'prod';
    }
    if (!backendUrl) {
        return DEFAULT_ENVIRONMENT;
    }
    const normalized = normalizeDevHostUrl(backendUrl);
    const localUrls = new Set([
        normalizeDevHostUrl(ENVIRONMENT_CONFIGS.local.backendBaseUrl),
        normalizeDevHostUrl(resolveLocalBackendBaseUrl()),
    ]);
    const lanHost = Config.LOCAL_DEV_HOST?.trim();
    if (lanHost) {
        localUrls.add(normalizeDevHostUrl(lanHost));
    }
    if (localUrls.has(normalized)) {
        return 'local';
    }
    for (const [key, config] of Object.entries(ENVIRONMENT_CONFIGS)) {
        if (key === 'local') {
            continue;
        }
        if (normalizeDevHostUrl(config.backendBaseUrl) === normalized) {
            return key;
        }
    }
    return DEFAULT_ENVIRONMENT;
};
/**
 * When the web app is opened on localhost, default API + linking to local development
 * even if `.env` sets BACKEND_BASE_URL to test/prod (avoids signup vs API mismatch).
 */
export function getWebLocalDevNetworkOverride() {
    // Only the webpack web bundle has a real location bar. On iOS/Android, `window`
    // may exist without `location` (e.g. Bridgeless), which must not throw during store init.
    if (Platform.OS !== 'web') {
        return null;
    }
    if (typeof window === 'undefined') {
        return null;
    }
    const { location } = window;
    if (!location) {
        return null;
    }
    const h = location.hostname;
    if (h !== 'localhost' && h !== '127.0.0.1') {
        return null;
    }
    return {
        environment: 'local',
        backendBaseUrl: ENVIRONMENT_CONFIGS.local.backendBaseUrl,
        frontendBaseUrl: `${location.protocol}//${location.host}`,
    };
}
const stripTrailingSlash = (url) => url.endsWith('/') ? url.slice(0, -1) : url;
/**
 * D1.2: website account-creation URL. Prefer build-time SIGNUP_URL
 * (per-environment .env / .env.production); otherwise `{frontendBaseUrl}/signup`.
 * Never points at pricing, checkout, or billing.
 */
export function resolveSignupUrl(frontendBaseUrl, storeBuild = __STORE_BUILD__) {
    if (storeBuild) {
        return `${productionConfig.frontendBaseUrl}/signup`;
    }
    const override = Config.SIGNUP_URL?.trim();
    if (override) {
        return stripTrailingSlash(override);
    }
    return `${stripTrailingSlash(frontendBaseUrl)}/signup`;
}
